import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// components
import IconDown from '../icons/down';
import IconUp from '../icons/up';
import TableMenuItem from './item';

import usePrevious from '../../hooks/use-previous';

import { TableMenuProps } from './types';

// styles
import './menu.css';

export default function TableMenu({
  onMenuToggle,
  headerText,
  baseUrl,
  menuItems,
  onCategoryClick,
  onGroupClick,
  onPrimaryAccountClick,
  fieldNameCategoryId = 'categoryId',
  fieldNameGroupId = 'groupId',
  fieldNamePrimaryId = 'primaryId',
  otherSearch,
  counterIsHidden = false,
}: TableMenuProps): React.JSX.Element {
  const [openedTabs, setOpenedTabs] = useState<string[]>([]);
  const [searchParams] = useSearchParams();

  const prevOpenedTabs = usePrevious(openedTabs);

  const categoryId = searchParams.get(fieldNameCategoryId);
  const groupId = searchParams.get(fieldNameGroupId);
  const primaryId = searchParams.get(fieldNamePrimaryId);

  const handleMenuIsOpen = () => {
    let nextOpenedTabs = ([] as string[]).concat(openedTabs);

    if (nextOpenedTabs?.length) {
      nextOpenedTabs = [];
    } else {
      menuItems?.forEach((item) => {
        nextOpenedTabs.push(item.category.id);

        item.category?.groups?.forEach((group) => {
          nextOpenedTabs.push(group.id);
        });
      });
    }

    setOpenedTabs(nextOpenedTabs);
    onMenuToggle();
  };

  useEffect(() => {
    if (!openedTabs.length && !prevOpenedTabs) {
      const nextOpenedTabs = ([] as string[]).concat(openedTabs);

      if (categoryId && !nextOpenedTabs.find((tab) => tab === categoryId)) {
        nextOpenedTabs.push(categoryId);
      }

      if (groupId && !nextOpenedTabs.find((tab) => tab === groupId)) {
        nextOpenedTabs.push(groupId);
      }

      setOpenedTabs(nextOpenedTabs);
    }
  }, [categoryId, groupId, openedTabs, prevOpenedTabs]);

  return (
    <div className="table-menu">
      <div className="table-menu-header">
        <div className="table-menu-header-text">{headerText}</div>
        <button
          type="button"
          className={`table-menu-header-button ${
            openedTabs?.length > 0 ? 'open' : ''
          }`}
          onClick={handleMenuIsOpen}
          aria-label="Toggle"
        >
          <IconDown />
          <IconUp />
        </button>
      </div>
      {menuItems?.map((item) => (
        <TableMenuItem
          type="category"
          label={item.category.label}
          to={`${baseUrl}?${fieldNameCategoryId}=${item.category.id}${
            otherSearch ? `&${otherSearch}` : ''
          }`}
          counter={item.category.counter}
          key={item.category.id}
          setOpenedTabs={setOpenedTabs}
          openedTabs={openedTabs}
          id={item.category.id}
          number={item.category.number}
          groups={item?.category?.groups}
          onClick={onCategoryClick}
          counterIsHidden={counterIsHidden}
          selectedId={categoryId}
        >
          {item?.category?.groups
            ? item.category.groups.map((group) => (
                <TableMenuItem
                  type="group"
                  label={group.label}
                  to={`${baseUrl}?${fieldNameCategoryId}=${
                    item.category.id
                  }&${fieldNameGroupId}=${group.id}${
                    otherSearch ? `&${otherSearch}` : ''
                  }`}
                  counter={group.counter}
                  key={group.id}
                  setOpenedTabs={setOpenedTabs}
                  openedTabs={openedTabs}
                  id={group.id}
                  number={`${item.category.number}.${group.number}`}
                  onClick={() =>
                    onGroupClick &&
                    onGroupClick({
                      categoryId: item?.category.id,
                      id: group.id,
                    })
                  }
                  selectedId={groupId}
                  counterIsHidden={counterIsHidden}
                >
                  {group?.accounts
                    ? group.accounts.map((account) => (
                        <TableMenuItem
                          type="primary-account"
                          label={account.label}
                          to={`${baseUrl}?${fieldNameCategoryId}=${
                            item.category.id
                          }&${fieldNameGroupId}=${
                            group.id
                          }&${fieldNamePrimaryId}=${account.id}${
                            otherSearch ? `&${otherSearch}` : ''
                          }`}
                          counter={account.counter}
                          key={account.id}
                          setOpenedTabs={setOpenedTabs}
                          openedTabs={openedTabs}
                          id={account.id}
                          number={`${item.category.number}.${group.number}.${account.number}`}
                          onClick={() =>
                            onPrimaryAccountClick &&
                            onPrimaryAccountClick({
                              categoryId: item?.category.id,
                              groupId: group.id,
                              id: account.id,
                            })
                          }
                          selectedId={primaryId}
                          counterIsHidden={counterIsHidden}
                        />
                      ))
                    : undefined}
                </TableMenuItem>
              ))
            : undefined}
        </TableMenuItem>
      ))}
    </div>
  );
}
